import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Trash2, CheckCircle, Bell } from 'lucide-react';
import { apiCall } from '../api';
import { useNotifications } from '../hooks/useNotifications';
import NotificationToast from './NotificationToast';
import './NotificationsPanel.css';

const NotificationsPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addListener } = useNotifications();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [toastNotification, setToastNotification] = useState(null);
  const dropdownRef = useRef(null);
  const previousCountRef = useRef(0);  // Track previous notification count

  // Determine pipeline based on current route
  const isV1Pipeline = location.pathname.startsWith('/v1');
  const pipelineFilter = isV1Pipeline ? 'v1' : 'main';

  // Fetch notifications on mount and every 30 seconds
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Set up SSE stream listener for real-time notifications (just update the list)
  useEffect(() => {
    const handleNewNotification = (newNotification) => {
      console.log('[NotificationsPanel] 📨 New notification received (SSE):', newNotification.type);
      // Just update the list - toast will be shown in fetchNotifications when count increases
      setNotifications((prev) => [newNotification, ...prev]);
      setUnreadCount((prev) => prev + 1);
    };

    // Add listener for new notifications
    const removeListener = addListener(handleNewNotification);

    // Cleanup
    return () => {
      removeListener();
    };
  }, [addListener]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const data = await apiCall(`/api/notifications?skip=0&limit=20&team=true&pipeline=${pipelineFilter}`);

      if (data && data.data) {
        const newCount = data.data.length;
        const previousCount = previousCountRef.current;

        console.log(`[NotificationsPanel] 📊 Notification count: ${previousCount} → ${newCount}`);

        setNotifications(data.data);
        setUnreadCount(data.unreadCount || 0);

        // When count increases, show toast for UNREAD notification and trigger work orders refresh if needed
        if (newCount > previousCount) {
          // Find the first unread notification to show in toast
          const unreadNotif = data.data.find((notif) => !notif.read);

          if (unreadNotif) {
            console.log('[NotificationsPanel] 🎯 Unread notification detected - showing toast:', unreadNotif.title);
            setToastNotification(unreadNotif);

            // If it's an extraction notification, also trigger work orders refresh
            if (unreadNotif.type === 'extraction') {
              console.log('[NotificationsPanel] 🔄 Extraction notification - triggering work orders refresh');

              // Store refresh signal in localStorage (for when WorkOrdersList mounts later)
              const refreshSignal = {
                timestamp: Date.now(),
                workOrderId: unreadNotif.relatedId,
                type: unreadNotif.type,
                title: unreadNotif.title
              };
              localStorage.setItem('workOrdersRefreshSignal', JSON.stringify(refreshSignal));
              console.log('[NotificationsPanel] ✅ Refresh signal stored in localStorage');

              // Dispatch event for immediate refresh if WorkOrdersList is currently mounted
              window.dispatchEvent(new CustomEvent('refreshWorkOrders', {
                detail: refreshSignal
              }));
              console.log('[NotificationsPanel] ✅ Refresh event dispatched');
            }
          } else {
            console.log('[NotificationsPanel] ℹ️ No unread notifications to show in toast');
          }
        } else {
          console.log('[NotificationsPanel] ℹ️ No new notifications (count same or decreased)');
        }

        // Update previous count for next comparison
        previousCountRef.current = newCount;
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      // Find the notification being marked as read
      const notifToRead = notifications.find((n) => n._id === notificationId);

      if (!notifToRead) return;

      console.log('[NotificationsPanel] 📖 Marking notification as read:', notifToRead.title);

      // Mark the main notification as read
      await apiCall(`/api/notifications/${notificationId}/read`, { method: 'PUT' });

      // Only for "Extraction Completed" notifications - mark all related notifications as read too
      if (notifToRead.title.includes('Extraction Completed') && notifToRead.relatedId) {
        console.log('[NotificationsPanel] 🔄 Extraction Completed - marking all related notifications');

        const relatedNotifications = notifications.filter(
          (n) => n.relatedId === notifToRead.relatedId && !n.read && n._id !== notificationId
        );

        console.log('[NotificationsPanel] 📋 Found', relatedNotifications.length, 'related unread notifications');

        // Mark all related notifications as read
        for (const relatedNotif of relatedNotifications) {
          await apiCall(`/api/notifications/${relatedNotif._id}/read`, { method: 'PUT' });
        }

        // Update local state - mark main notification and all related ones as read
        setNotifications((prev) =>
          prev.map((notif) =>
            notif._id === notificationId || notif.relatedId === notifToRead.relatedId
              ? { ...notif, read: true }
              : notif
          )
        );
      } else {
        // For other notifications, just mark the single notification as read
        setNotifications((prev) =>
          prev.map((notif) =>
            notif._id === notificationId ? { ...notif, read: true } : notif
          )
        );
      }

      // Fetch unread count
      const data = await apiCall(`/api/notifications/count?team=true&pipeline=${pipelineFilter}`);
      setUnreadCount(data.unreadCount || 0);
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await apiCall(`/api/notifications/mark-all-read?team=true&pipeline=${pipelineFilter}`, { method: 'PUT' });

      // Update local state
      setNotifications((prev) =>
        prev.map((notif) => ({ ...notif, read: true }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await apiCall(`/api/notifications/${notificationId}`, { method: 'DELETE' });

      // Update local state
      setNotifications((prev) =>
        prev.filter((notif) => notif._id !== notificationId)
      );

      // Fetch unread count
      const data = await apiCall(`/api/notifications/count?team=true&pipeline=${pipelineFilter}`);
      setUnreadCount(data.unreadCount || 0);
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read when clicking on it
    if (!notification.read) {
      try {
        console.log('[NotificationsPanel] 📖 Clicking notification - marking as read:', notification.title);
        await handleMarkAsRead(notification._id);
      } catch (err) {
        console.error('Error marking notification as read on click:', err);
      }
    }

    // Navigate to work order detail if relatedId exists
    if (notification.relatedId) {
      // Navigate to the correct pipeline's work order page
      const workOrderPath = isV1Pipeline 
        ? `/v1/work-order/${notification.relatedId}`
        : `/work-order/${notification.relatedId}`;
      navigate(workOrderPath);
      setIsOpen(false); // Close dropdown after navigation
    }
  };

  const handleClearAll = async () => {
    if (notifications.length === 0) return;

    // Confirm before clearing all
    if (!window.confirm('Are you sure you want to delete all notifications?')) {
      return;
    }

    try {
      await apiCall(`/api/notifications/clear-all?team=true&pipeline=${pipelineFilter}`, { method: 'DELETE' });

      // Clear notifications from state
      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      console.error('Error clearing all notifications:', err);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  return (
    <>
      {/* Toast Notification */}
      {toastNotification && (
        <NotificationToast
          notification={toastNotification}
          onClose={() => {
            console.log('[NotificationsPanel] 🗑️ Closing toast:', toastNotification.title);
            setToastNotification(null);
          }}
        />
      )}

      <div className="notifications-panel" ref={dropdownRef}>
      {/* Notification Bell Button */}
      <button
        className="notifications-btn"
        onClick={() => setIsOpen(!isOpen)}
        title="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
      </button>

      {/* Notifications Dropdown */}
      {isOpen && (
        <div
          className="notifications-dropdown"
          style={{
            width: '380px',
            maxHeight: '500px',
            background: '#ffffff',
            borderColor: '#e5e7eb'
          }}
        >
          {/* Header */}
          <div
            className="notifications-header"
            style={{
              padding: '12px 14px',
              minHeight: '44px',
              background: '#ffffff',
              borderBottomColor: '#e5e7eb',
              borderBottomStyle: 'solid',
              borderBottomWidth: '1px'
            }}
          >
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#000000' }}>Notifications</h3>
            <div className="header-actions">
              {unreadCount > 0 && (
                <button
                  className="mark-all-btn"
                  onClick={handleMarkAllAsRead}
                  title="Mark all as read"
                >
                  <CheckCircle size={14} /> Mark All
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  className="clear-all-btn"
                  onClick={handleClearAll}
                  title="Delete all notifications"
                >
                  <X size={14} /> Clear
                </button>
              )}
            </div>
          </div>

          {/* Notifications List */}
          <div
            className="notifications-list"
            style={{
              background: '#ffffff',
              color: '#000000'
            }}
          >
            {loading && notifications.length === 0 ? (
              <div className="loading-state" style={{ color: '#000000', background: '#ffffff' }}>
                Loading notifications...
              </div>
            ) : notifications.length === 0 ? (
              <div
                className="empty-state"
                style={{
                  padding: '24px 16px',
                  minHeight: '120px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Bell size={32} style={{ opacity: 0.3, width: '32px', height: '32px' }} />
                <p style={{ margin: 0, fontSize: '13px', color: '#000000' }}>
                  No notifications yet
                </p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif._id}
                  className={`notification-item ${notif.read ? 'read' : 'unread'}`}
                  onClick={() => handleNotificationClick(notif)}
                  style={{
                    borderLeftColor: notif.color || '#3b82f6',
                    background: '#ffffff',
                    borderBottomColor: '#f3f4f6',
                    color: '#000000',
                    cursor: notif.relatedId ? 'pointer' : 'default'
                  }}
                >
                  <div className="notification-content">
                    <div className="notification-header">
                      <span className="icon">{notif.icon}</span>
                      <span className="title" style={{ color: '#000000' }}>{notif.title}</span>
                    </div>
                    <p className="message" style={{ color: '#000000' }}>{notif.message}</p>
                    <span className="time" style={{ color: '#000000' }}>
                      {new Date(notif.createdAt).toLocaleString()}
                    </span>
                  </div>

                  <div className="notification-actions">
                    {!notif.read && (
                      <button
                        className="action-btn mark-read"
                        onClick={() => handleMarkAsRead(notif._id)}
                        title="Mark as read"
                      >
                        <CheckCircle size={14} />
                      </button>
                    )}
                    <button
                      className="action-btn delete"
                      onClick={() => handleDeleteNotification(notif._id)}
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div
              className="notifications-footer"
              style={{
                background: '#f9fafb',
                borderTopColor: '#e5e7eb',
                color: '#000000'
              }}
            >
              <small>{unreadCount === 0 ? 'All caught up!' : `${unreadCount} unread`}</small>
            </div>
          )}
        </div>
      )}
      </div>
    </>
  );
};

export default NotificationsPanel;
